import type {
    LocalizeContextType
} from "../../types/locale";

export type SwitcherVariantType<N extends PropertyKey> = Record<N, unknown> & {
    title?: string;
};

export type SwitcherVariantsConstantType<N extends PropertyKey, V extends SwitcherVariantType<N>> = {
    localize: LocalizeContextType["localize"];
    isShowNextVariant?: boolean;
    defaultVariants?: Array<V>;
    variants?: Array<V>;
    keys: Array<V[N]>;
    activeKey: V[N];
    title?: string;
    keyName: N;
};

export type SwitcherVariantsType<N extends PropertyKey, V extends SwitcherVariantType<N>> = {
    accessibilityLabel: string;
    currentTitle: string;
    currentIndex: number;
    displayVariant?: V;
    currentVariant?: V;
    variants: Array<V>;
    nextIndex: number;
    nextVariant?: V;
    nextKey?: V[N];
};

type SwitcherKeyOrderConstantType<K> = {
    keys: Array<K>;
    secondKey: K;
    firstKey: K;
};

type SwitcherNextIndexConstantType<K> = {
    variantKeys: Array<K>;
    currentIndex: number;
    keys: Array<K>;
    activeKey: K;
};

const compareSwitcherKeys = <K>({
    secondKey,
    firstKey,
    keys
}: SwitcherKeyOrderConstantType<K>): number => {
    const secondIndex = keys.indexOf(secondKey);

    const firstIndex = keys.indexOf(firstKey);

    if(firstIndex === -1 && secondIndex !== -1) {
        return 1;
    }

    if(secondIndex === -1 && firstIndex !== -1) {
        return -1;
    }

    if(firstIndex === -1 && secondIndex === -1) {
        return String(firstKey).localeCompare(String(secondKey));
    }

    return firstIndex - secondIndex;
};

const getSwitcherNextIndex = <K>({
    currentIndex,
    variantKeys,
    activeKey,
    keys
}: SwitcherNextIndexConstantType<K>): number => {
    if(!variantKeys.length) {
        return -1;
    }

    if(currentIndex !== -1) {
        return (currentIndex + 1) % variantKeys.length;
    }

    const followingIndex = variantKeys.findIndex((variantKey) => {
        return compareSwitcherKeys({
            firstKey: variantKey,
            secondKey: activeKey,
            keys
        }) > 0;
    });

    return followingIndex === -1 ? 0 : followingIndex;
};

export const getSwitcherVariants = <N extends PropertyKey, V extends SwitcherVariantType<N>>({
    isShowNextVariant = false,
    defaultVariants = [],
    variants = [],
    activeKey,
    localize,
    keyName,
    title,
    keys
}: SwitcherVariantsConstantType<N, V>): SwitcherVariantsType<N, V> => {
    const currentVariants = [
        ...variants,
        ...defaultVariants
    ].filter((variant, index, allVariants) => {
        return allVariants.findIndex((otherVariant) => otherVariant[keyName] === variant[keyName]) === index;
    }).sort((firstVariant, secondVariant) => {
        return compareSwitcherKeys({
            secondKey: secondVariant[keyName],
            firstKey: firstVariant[keyName],
            keys
        });
    });

    const variantKeys = currentVariants.map((variant) => variant[keyName]);

    const currentIndex = variantKeys.indexOf(activeKey);

    const nextIndex = getSwitcherNextIndex({
        currentIndex,
        variantKeys,
        activeKey,
        keys
    });

    const currentVariant = currentVariants[currentIndex];

    const nextVariant = currentVariants[nextIndex];

    const currentTitle = currentVariant && currentVariant.title ? currentVariant.title : String(activeKey);

    return {
        displayVariant: isShowNextVariant ? nextVariant : currentVariant ?? nextVariant,
        accessibilityLabel: title ? localize("switcher-accessibility-label", [
            title,
            currentTitle
        ]) : localize("switcher-value-accessibility-label", [
            currentTitle
        ]),
        nextKey: nextVariant ? nextVariant[keyName] : undefined,
        variants: currentVariants,
        currentVariant,
        currentIndex,
        currentTitle,
        nextVariant,
        nextIndex
    };
};
